import { useCallback, useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  TextField,
} from "@mui/material";

import * as yup from "yup";
import { Formik } from "formik";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../../Common/styles/modal";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import http from "../../../utils/http";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import isEmail from "validator/es/lib/isEmail";
import { setParticipantToggle } from "../../../Redux/reducers/participantsSlice";
import { DrawerForm } from "../../Common/styles/drawer";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { RefreshOutlined } from "@mui/icons-material";

let schema = yup.object().shape({
  firstName: yup.string().optional(),
  lastName: yup.string().optional(),
  email: yup
    .string()
    .trim()
    .test("is-valid", "*Please enter a valid Email ID.", (value) =>
      value ? isEmail(value) : true
    ),
  joinCode: yup.string().required("Join Code is required"),
  subjectId: yup.string().trim().required("Participant ID is required"),
  siteId: yup.string().required("Site is Required"),
  lang: yup.string().required("Language is Required"),
});

type Props = {
  showModal: boolean;
  closeModal: () => void;
};

type ConfirmProps = {
  show: boolean;
  onClose: () => void;
  onOk: () => void;
};

const ConfirmModal = ({ onOk, show, onClose }: ConfirmProps) => {
  const handleOk = () => {
    onClose();
    onOk();
  };

  return (
    <Dialog open={show} onClose={onClose}>
      <DialogTitle id="alert-dialog-title">
        Confirm Participant Email
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          You are proceeding without entering Email for this participant.
          Continuing so, this participant will be not receiving survey emails
          and reminders. Do you want to proceed?
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          No
        </Button>
        <Button variant="contained" onClick={handleOk} autoFocus>
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AddRecordModal = ({ showModal, closeModal }: Props) => {
  const [emailConfirmation, setEmailConfirmation] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const { id } = useParams();
  const sitesList = useAppSelector((state) => state.study.sitesList);
  const languages = useAppSelector((state) => state.study.languages);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [participantsLoading, setParticipantsLoading] = useState(true);
  const [code, setCode] = useState("");
  const [participantId, setParticipantId] = useState<string>("");
  const idGeneration = useAppSelector(
    (state) => state.study.subjectIdGeneration
  );

  const participantSubjectId = useCallback(
    async (siteId: string): Promise<string> => {
      setParticipantsLoading(true);
      const body = {
        siteId,
        studyId: id,
      };
      const res: AxiosResponse = await http.post(
        `/participants/generate-subject-id`,
        body
      );
      const data = res.data.data.subjectId;
      setParticipantId(data);
      setParticipantsLoading(false);
      return data;
    },
    [id]
  );

  const submitHandler = async (values: any) => {
    try {
      if (!values.email && !emailConfirmation) {
        setShowEmail(true);
        return;
      }
      setSubmitLoader(true);
      let res: AxiosResponse;
      const body = {
        ...values,
        studyId: id,
      };
      if (!body.email) {
        delete body.email;
      }
      if (!body.phoneNumber) {
        delete body.phoneNumber;
      } else {
        body.phoneNumber = "+" + body.phoneNumber;
      }
      res = await http.post(`/participants`, body);

      toastMessage("success", res.data.message);
      closeModal();
      setSubmitLoader(false);
      dispatch(setParticipantToggle());
    } catch (err) {
      setSubmitLoader(false);
      errorToastMessage(err as Error);
    }
  };

  useEffect(() => {
    const fetchCode = async () => {
      try {
        setLoading(true);
        let url = `/participants/generate-join-code`;
        const body = {
          studyId: id,
        };
        const res1 = await http.post(url, body);
        const code = res1?.data?.data?.joinCode?.toString();
        const initialSite =
          sitesList?.length > 0
            ? sitesList?.find((site: any) => site.isMainSite)
              ? sitesList?.find((site: any) => site.isMainSite === true)?.id
              : sitesList?.[0]?.id
            : "";

        const subjectId =
          idGeneration !== "free_text"
            ? await participantSubjectId(initialSite)
            : "";
        setCode(code);
        setParticipantId(subjectId);
        setLoading(false);
        setParticipantsLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchCode();
  }, [id, idGeneration, participantSubjectId, sitesList]);

  const handleSiteChange = async (
    setFieldValue: Function,
    event: any,
    oldValue: string
  ) => {
    const newValue = event.target.value;
    setFieldValue("siteId", newValue);
    if (idGeneration === "free_text") return;
    try {
      setParticipantsLoading(true);
      const subjectId = await participantSubjectId(event.target.value);
      setFieldValue("subjectId", subjectId);
    } catch (e) {
      errorToastMessage(e as Error);
      setFieldValue("siteId", oldValue);
    } finally {
      setParticipantsLoading(false);
    }
  };

  const toggleIdRefresh = async (setFieldValue: Function, values: any) => {
    try {
      setParticipantsLoading(true);
      const subjectId = await participantSubjectId(values.siteId);
      setFieldValue("subjectId", subjectId);
    } catch (e) {
      errorToastMessage(e as Error);
    } finally {
      setParticipantsLoading(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader title="Create New Participant" onCloseClick={closeModal} />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <CircularProgress size={25} />
          </Box>
        ) : (
          <Formik
            initialValues={{
              firstName: "",
              lastName: "",
              email: "",
              phoneNumber: "",
              joinCode: code,
              subjectId: participantId || "",
              siteId:
                sitesList?.length > 0
                  ? sitesList?.find((site: any) => site.isMainSite)
                    ? sitesList?.find((site: any) => site.isMainSite === true)
                        ?.id
                    : sitesList?.[0]?.id
                  : "",
              lang: "en",
            }}
            validationSchema={schema}
            onSubmit={(values) => {
              submitHandler(values);
            }}
          >
            {({
              handleSubmit,
              getFieldProps,
              setFieldValue,
              values,
              errors,
              touched,
            }) => (
              <form onSubmit={handleSubmit} style={DrawerForm}>
                <Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <FormControl sx={InputWrapper}>
                      <FormLabel sx={LabelStyle} htmlFor="first-name">
                        First Name
                      </FormLabel>
                      <TextField
                        fullWidth
                        id="first-name"
                        {...getFieldProps("firstName")}
                        error={
                          touched?.firstName && errors?.firstName ? true : false
                        }
                        helperText={
                          touched?.firstName && errors?.firstName
                            ? (errors?.firstName as string)
                            : " "
                        }
                        placeholder="First Name"
                      />
                    </FormControl>
                    <FormControl sx={InputWrapper}>
                      <FormLabel sx={LabelStyle} htmlFor="last-name">
                        Last Name
                      </FormLabel>
                      <TextField
                        fullWidth
                        id="last-name"
                        {...getFieldProps("lastName")}
                        error={
                          touched?.lastName && errors?.lastName ? true : false
                        }
                        helperText={
                          touched?.lastName && errors?.lastName
                            ? (errors?.lastName as string)
                            : " "
                        }
                        placeholder="Last Name"
                      />
                    </FormControl>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <FormControl sx={InputWrapper}>
                      <FormLabel sx={LabelStyle} htmlFor="email">
                        Participant Email{" "}
                        <span style={{ color: "red" }}>*</span>
                      </FormLabel>
                      <TextField
                        fullWidth
                        id="email"
                        {...getFieldProps("email")}
                        error={touched?.email && errors?.email ? true : false}
                        helperText={
                          touched?.email && errors?.email
                            ? (errors?.email as string)
                            : " "
                        }
                        placeholder="Enter an email address"
                      />
                    </FormControl>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <FormControl sx={InputWrapper}>
                      <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                        <FormLabel sx={LabelStyle} htmlFor="id">
                          Participant ID <span style={{ color: "red" }}>*</span>
                        </FormLabel>
                        {idGeneration !== "free_text" && (
                          <Box>
                            <IconButton
                              onClick={() =>
                                toggleIdRefresh(setFieldValue, values)
                              }
                            >
                              <RefreshOutlined sx={{ fontSize: 21, mb: 0.5 }} />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      <TextField
                        id="id"
                        {...getFieldProps("subjectId")}
                        error={
                          touched?.subjectId && errors?.subjectId ? true : false
                        }
                        placeholder="Enter Participant ID"
                        inputProps={{ maxLength: 12 }}
                        InputProps={{
                          readOnly: idGeneration !== "free_text",
                          endAdornment: participantsLoading ? (
                            <InputAdornment position="end">
                              <CircularProgress size={20} color="primary" />
                            </InputAdornment>
                          ) : null,
                        }}
                        helperText={
                          touched?.subjectId && errors?.subjectId
                            ? (errors?.subjectId as string)
                            : " "
                        }
                      />
                    </FormControl>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <FormControl sx={InputWrapper}>
                      <FormLabel sx={LabelStyle} htmlFor="site">
                        Site <span style={{ color: "red" }}>*</span>
                      </FormLabel>
                      <Select
                        id="site"
                        value={values?.siteId}
                        onChange={(e) =>
                          handleSiteChange(setFieldValue, e, values?.siteId)
                        }
                        MenuProps={{
                          PaperProps: {
                            style: { maxHeight: "300px", overflowY: "auto" },
                          },
                        }}
                      >
                        {sitesList?.map((site) => (
                          <MenuItem key={site.id} value={site.id}>
                            {site.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {touched?.siteId && errors?.siteId
                          ? (errors?.siteId as string)
                          : " "}
                      </FormHelperText>
                    </FormControl>
                  </Box>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle} htmlFor="language">
                      Language <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Select
                      id="language"
                      value={values?.lang}
                      onChange={(e) => setFieldValue(`lang`, e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          style: { maxHeight: "300px", overflowY: "auto" },
                        },
                      }}
                    >
                      {languages?.map((lang) => (
                        <MenuItem
                          key={lang.languageCode}
                          value={lang.languageCode}
                        >
                          {lang.languageLabel}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {touched?.lang && errors?.lang
                        ? (errors?.lang as string)
                        : " "}
                    </FormHelperText>
                  </FormControl>
                  <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                    <FormControl sx={InputWrapper}>
                      <FormLabel sx={LabelStyle}>Phone Number</FormLabel>
                      <PhoneInput
                        country={"sg"}
                        inputStyle={{
                          height: "48px",
                          width: "100%",
                          borderColor: "#e0e3eb",
                        }}
                        value={values.phoneNumber}
                        onChange={(phone) =>
                          setFieldValue("phoneNumber", phone)
                        }
                      />
                    </FormControl>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <FormControl sx={InputWrapper}>
                      <FormLabel sx={LabelStyle} htmlFor="join-code">
                        Join Code
                      </FormLabel>
                      <TextField
                        fullWidth
                        id="join-code"
                        {...getFieldProps("joinCode")}
                        error={
                          touched?.joinCode && errors?.joinCode ? true : false
                        }
                        helperText={
                          touched?.joinCode && errors?.joinCode
                            ? (errors?.joinCode as string)
                            : " "
                        }
                        InputProps={{ readOnly: true }}
                        placeholder="Join Code"
                      />
                    </FormControl>
                  </Box>
                </Box>
                <Box sx={{ mt: "auto" }}>
                  <Divider />
                  <Box sx={ModalDrawerButtonStyles}>
                    {!submitLoader ? (
                      <>
                        <Button onClick={closeModal} variant="outlined">
                          Cancel
                        </Button>
                        <Button type="submit" variant="contained">
                          Save
                        </Button>
                      </>
                    ) : (
                      <CircularProgress size={25} />
                    )}
                  </Box>
                </Box>
                {showEmail && (
                  <ConfirmModal
                    show={showEmail}
                    onClose={() => setShowEmail(false)}
                    onOk={() => {
                      setEmailConfirmation(true);
                      handleSubmit();
                    }}
                  />
                )}
              </form>
            )}
          </Formik>
        )}
      </Box>
    </Modal>
  );
};

export default AddRecordModal;
